'use client';

import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faHospital,
  faScalpel,
  faBedPulse,
  faCircleDollar,
  faBriefcaseMedical,
} from '@fortawesome/pro-regular-svg-icons';
import { Card } from 'src/components/PulseUI';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { formatCurrency } from 'src/utils/formatCurrency/formatCurrency';
import WidgetHeader from 'src/components/Profiles/shared/widgets/WidgetHeader';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface MetricItem {
  icon: IconDefinition;
  label: string;
  value: string;
}

const CompactSystemMetrics: React.FC = () => {
  const { entityDetails } = useProfile();

  const facilityMetrics: MetricItem[] = [
    {
      icon: faHospital,
      label: 'Sites',
      value: (entityDetails?.total_sites || 0).toLocaleString(),
    },
    {
      icon: faScalpel,
      label: 'Operating Rooms',
      value: (entityDetails?.operating_room_count || 0).toLocaleString(),
    },
    {
      icon: faBedPulse,
      label: 'Beds',
      value: (entityDetails?.total_bed_count || 0).toLocaleString(),
    },
  ];

  const financialMetrics: MetricItem[] = [
    {
      icon: faCircleDollar,
      label: 'Total Patient Revenue',
      value: formatCurrency(entityDetails?.total_patient_revenue || 0),
    },
    {
      icon: faBriefcaseMedical,
      label: 'Current Assets',
      value: formatCurrency(entityDetails?.total_current_assets || 0),
    },
    {
      icon: faBriefcaseMedical,
      label: 'Total Assets',
      value: formatCurrency(entityDetails?.total_assets || 0),
    },
    {
      icon: faBriefcaseMedical,
      label: 'Total Liability',
      value: formatCurrency(entityDetails?.total_current_liabilities || 0),
    },
  ];

  return (
    <Card
      className="p-4 flex flex-col gap-3 bg-white h-full"
      data-testid="compact-system-metrics"
    >
      <WidgetHeader fallbackTitle="System Metrics" />

      <div
        className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-3"
        role="list"
        aria-label="System Metrics"
      >
        {/* Facility counts — compact inline bar */}
        <div
          className="flex items-center gap-3 rounded-lg bg-neutral-50 px-3 py-2"
          role="listitem"
          data-testid="compact-system-metrics-facility-bar"
        >
          {facilityMetrics.map((metric, index) => (
            <React.Fragment key={metric.label}>
              {index > 0 && (
                <div
                  className="h-1 w-1 rounded-full bg-neutral-200 flex-shrink-0"
                  aria-hidden="true"
                />
              )}
              <div className="flex items-center gap-1.5">
                <Icon
                  icon={metric.icon}
                  className="h-3 w-3 text-secondary"
                  aria-hidden="true"
                />
                <span className="text-xs text-secondary">{metric.label}</span>
                <span className="text-xs font-bold text-primary tabular-nums">
                  {metric.value}
                </span>
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Financial metrics — full-width rows */}
        {financialMetrics.map((metric) => (
          <div
            key={metric.label}
            className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg bg-neutral-50"
            role="listitem"
            data-testid={`compact-system-metrics-financial-${metric.label}`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <Icon
                icon={metric.icon}
                className="h-4 w-4 text-sky-600 flex-shrink-0"
                aria-hidden="true"
              />
              <span className="text-sm font-medium text-neutral-600 truncate">
                {metric.label}
              </span>
            </div>
            <span className="text-lg font-bold text-neutral-800 tabular-nums flex-shrink-0">
              {metric.value}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default CompactSystemMetrics;
